import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCircle }        from '@react-icons/all-files/fa/FaCircle';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }           from '@react-icons/all-files/fa/FaCog';
import { FaHome }          from '@react-icons/all-files/fa/FaHome';
import Breadcrumb  from '../../../../commons/Breadcrumb';
import PageHeader  from '../../../../commons/PageHeader';
import PageForm, { FormField } from '../../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/workflow-actividad.reducer';

const BACK = '/configuracion/workflow/nodos';

interface NodoForm {
  id:          string;
  nombre:      string;
  descripcion: string;
  activo:      string;
}

const empty: NodoForm = { id: '', nombre: '', descripcion: '', activo: 'true' };

const fields: FormField<NodoForm>[] = [
  { name: 'id',          label: 'ID',          readOnly: true, editOnly: true },
  { name: 'nombre',      label: 'Nombre',      required: true, placeholder: 'Nombre del nodo' },
  { name: 'descripcion', label: 'Descripción', type: 'textarea', rows: 3, placeholder: 'Descripción del nodo' },
  { name: 'activo',      label: 'Activo',      type: 'checkbox' },
];

export default function NodosFormPage() {
  const { id }   = useParams<{ id: string }>();
  const isNew    = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity        = useAppSelector(s => s.workflowActividad.entity)   as any;
  const updating      = useAppSelector(s => s.workflowActividad.updating);
  const updateSuccess = useAppSelector(s => s.workflowActividad.updateSuccess);

  const [form,   setForm]   = useState<NodoForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof NodoForm, string>>>({});

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id:          String(entity.id),
        nombre:      entity.nombre      ?? '',
        descripcion: entity.descripcion ?? '',
        activo:      String(entity.activo ?? true),
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew ? 'Nodo creado correctamente.' : 'Nodo actualizado correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (name: keyof NodoForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const e2: Partial<Record<keyof NodoForm, string>> = {};
    if (!form.nombre.trim()) e2.nombre = 'El nombre es requerido';
    if (Object.keys(e2).length) { setErrors(e2); return; }

    const payload: any = {
      nombre:      form.nombre,
      descripcion: form.descripcion || null,
      activo:      form.activo === 'true',
    };
    if (!isNew) payload.id = Number(id);

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio',        to: '/',                       icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion',          icon: <FaCog /> },
        { label: 'Workflow',      to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Nodos',         to: BACK,                      icon: <FaCircle /> },
        { label: isNew ? 'Nuevo Nodo' : `Editar Nodo #${id}` },
      ]} />

      <PageHeader
        icon={<FaCircle />}
        iconColor="#10b981"
        title={isNew ? 'Nuevo Nodo' : 'Editar Nodo'}
        subtitle={isNew ? 'Completa los campos para crear un nuevo nodo' : `Modificando nodo #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#10b981"
      />
    </div>
  );
}
